import { type CSSProperties, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useWorkspace } from '../../data/hooks';
import { Button, FormField, Icon, Input } from '../../ui';
import { AuthLayout } from './AuthLayout';
import { authErrorMessage } from './authMessages';
import { FormError } from './FormError';
import { useLogin } from './useSession';

/**
 * THE FORM'S DEFAULT BUTTON, AND IT IS THE KIT'S TINTED ONE (E3, ledger E-W4-46).
 *
 * `ui/Button`'s `push` variant is the kit's CONTENT-AREA button - a flat fill, no glass, no
 * rim - and these three declarations turn it into the kit's DEFAULT member of that family:
 * `Buttons/{Light,Dark}/Content Area/Bordered Default/4 Lg/Active, Off, {1 Idle, 3 Clicked}`,
 * read out of the kit file itself: light #0088FF idle and #0078F0 clicked, dark #0091FF and
 * #0A99FF, label #FFFFFF in both. The product already carries all four as
 * `--color-shell-prominent`, `--color-shell-prominent-pressed` and
 * `--color-shell-selected-ink`, so this is a re-POINTING of `.push-control`'s own variables
 * and not a new colour - which is also what keeps the PRESS working: `.push-control:active`
 * reads `--color-content-button-pressed`, so the clicked fill arrives through the same
 * cascade as the idle one. (`--color-ink` is the label: `.push-control` sets
 * `color: var(--color-ink)`, and a custom property set here is what its descendants,
 * including the spinner's `currentColor`, inherit.)
 *
 * WHY THE CONTENT-AREA FAMILY AND NOT THE OVER-GLASS ONE the spec's section 2 item 5 names:
 * this form stands on the window BODY, and the HIG is flat about the content layer - "Don't
 * use Liquid Glass in the content layer ... use standard materials for elements in the
 * content layer" (hig: https://developer.apple.com/design/human-interface-guidelines/materials).
 * It is the same reading that gave the list its push buttons (L-B2).
 */
export const DEFAULT_BUTTON = {
  '--color-content-button': 'var(--color-shell-prominent)',
  '--color-content-button-pressed': 'var(--color-shell-prominent-pressed)',
  '--color-ink': 'var(--color-shell-selected-ink)',
} as CSSProperties;

/**
 * OFFLINE IS THE BROWSER'S OWN ANSWER, read here rather than inferred from a failed request
 * - the notice `spec/components/MAP.md` describes is about the CONNECTION, not about this
 * sign-in. The same hook the reports screen uses (`app/reports/ReportsScreen.tsx`).
 */
function useOffline(): boolean {
  const [offline, setOffline] = useState(
    () => typeof navigator !== 'undefined' && navigator.onLine === false,
  );
  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  return offline;
}

/** The address is checked here only for the one thing a browser can check without asking. */
const EMAIL_ERROR = 'Check the address; it needs an @';

export interface LoginFormProps {
  readonly email: string;
  readonly password: string;
  readonly onEmailChange: (value: string) => void;
  readonly onPasswordChange: (value: string) => void;
  readonly onSubmit: () => void;
  /** Beside the field it belongs to (spec section 2 item 7). */
  readonly fieldError?: string | undefined;
  /** Under the button: a refusal is about the pair, not about one field. */
  readonly formError?: string | undefined;
  readonly pending?: boolean | undefined;
  readonly offline?: boolean | undefined;
  /** Only the real screen takes focus on open; the states gallery renders many of these. */
  readonly takeFocus?: boolean | undefined;
}

/**
 * THE SIGN-IN FORM ITSELF, exported because `/dev/auth-states` draws its states from the
 * built component rather than from a replica of it.
 *
 * THE ORDER IS THE SPEC'S: two labelled fields, the offline line, the one default button,
 * the refusal under it, and the recovery link last - which is also the tab order Apple's
 * sign-in asks for (section 5: Email, Password, Sign In, Forgot password).
 */
export function LoginForm({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  fieldError,
  formError,
  pending = false,
  offline = false,
  takeFocus = false,
}: LoginFormProps) {
  /** THE ONE ACTION IS UNAVAILABLE UNTIL THERE IS SOMETHING TO SEND (rulebook D9, R8). */
  const ready = email.trim() !== '' && password !== '';

  return (
    <form
      className="space-y-5"
      /**
       * THE BROWSER'S OWN BUBBLE IS TURNED OFF, and this is the round's one line about a web
       * pattern: `type="email"` makes Chromium refuse the submit and float its own tooltip
       * over the field ("Please include an '@' in the email address"), which is a bubble
       * nobody designed, in a typeface nobody chose, in one language. `noValidate` hands the
       * check back to the screen, which answers it beside the field the way the spec asks
       * (section 2 item 7). The field keeps `type="email"` for the phone keyboard.
       */
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <FormField label="Email" error={fieldError}>
        {(props) => (
          <Input
            {...props}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            /* R10: the hint is an EXAMPLE and the field keeps its label above it. */
            placeholder="name@example.com"
            /**
             * THE FIRST FIELD TAKES FOCUS on the real screen (spec section 5, audit P3-8:
             * `document.activeElement` was `BODY` on a screen whose only purpose is to be
             * typed into). Nothing above it is content to skip past.
             */
            autoFocus={takeFocus}
            disabled={pending}
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            invalid={fieldError !== undefined}
          />
        )}
      </FormField>

      <FormField label="Password">
        {(props) => (
          <Input
            {...props}
            type="password"
            autoComplete="current-password"
            disabled={pending}
            value={password}
            onChange={(event) => onPasswordChange(event.target.value)}
          />
        )}
      </FormField>

      {offline && (
        /*
          ONE PASSIVE LINE ABOVE THE BUTTON (spec section 4, `components/MAP.md` "Offline
          notice"): a warning symbol and one sentence, no button and no dismissal. It sits
          above the action because it is the reason the action will not work.
        */
        <p className="flex items-center gap-2 text-2xs text-ink-muted" role="status">
          <Icon name="warning" size="1rem" />
          You are offline. Sign in when the connection returns.
        </p>
      )}

      <Button
        type="submit"
        variant="push"
        block
        style={DEFAULT_BUTTON}
        disabled={!ready}
        loading={pending}
      >
        Sign in
      </Button>

      {/* UNDER THE BUTTON, NEVER A BANNER AT THE TOP (spec section 2 item 7, rulebook D4).
          It arrives and leaves on the Fade family (M3, `app/auth/FormError.tsx`). */}
      <FormError message={formError} />

      <p className="text-2xs text-ink-subtle">
        <Link to="/forgot-password" className="text-accent">
          Forgot password?
        </Link>
      </p>
    </form>
  );
}

/**
 * Sign in. Real credentials, real session cookie.
 *
 * `state.from` is where the guard sent us from, so an expired session lands the user back
 * on the record they were looking at instead of on a dashboard (spec section 2 item 8).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const login = useLogin();
  const offline = useOffline();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const serverError = login.error === null ? undefined : authErrorMessage(login.error);

  /**
   * THE SECOND LINE NAMES THE WORKSPACE WHEN THE INSTANCE HAS A NAME (spec section 2 item
   * 3): a person who reaches a sign-in form wants to know which installation they reached
   * before they type a password into it, which is what the HIG asks a heading to answer
   * ("A title helps people confirm their location").
   *
   * WHILE THE ANSWER IS IN FLIGHT AND WHEN THERE IS NONE, the existing sentence stands
   * (copy deck C11 keeps it) rather than an empty line that fills in half a second later:
   * the name arrives from one request over an open route, and a heading that reflows under
   * the reader is worse than one that never moved.
   */
  const workspace = useWorkspace();
  const name = workspace.data?.name ?? null;
  const subtitle = name ?? 'Use the account your administrator created.';

  const submit = () => {
    /**
     * THE ADDRESS IS CHECKED BEFORE THE SERVER IS, and only for the one thing that is not a
     * question about this instance: an address with no `@` cannot match any account
     * anywhere, and answering it here puts the error beside the field that has it instead
     * of under the button (spec section 2 item 7). Everything else - whether the account
     * exists, whether the password matches - is the server's, and deliberately vague.
     */
    if (!email.includes('@')) {
      setFieldError(EMAIL_ERROR);
      return;
    }
    setFieldError(undefined);
    login.mutate({ email, password }, { onSuccess: () => void navigate(from, { replace: true }) });
  };

  return (
    <AuthLayout title="Sign in" subtitle={subtitle}>
      <LoginForm
        email={email}
        password={password}
        onEmailChange={(value) => {
          setEmail(value);
          setFieldError(undefined);
        }}
        onPasswordChange={setPassword}
        onSubmit={submit}
        fieldError={fieldError}
        formError={fieldError === undefined ? serverError : undefined}
        pending={login.isPending}
        offline={offline}
        takeFocus
      />
    </AuthLayout>
  );
}
